import { ExclamationOutlined, LoadingOutlined } from '@ant-design/icons';
import ErrorHandler from 'common/error/components/error-handler';
import Table from 'common/table/legacy-table';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from 'ui';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import { absenceStatusses } from '../../../utils/app-configs';
import { deleteAbsence } from '../../containers/absence/absence-actions';
import AbsenceEditSidepanel from '../../containers/absence/absence-edit-sidepanel-container';
import AbsenceToolbar from '../../containers/absence/absence-toolbar-container';
import TypesMenu from '../../containers/types-menu-container';
import settingsConfig from '../../settings-config';

const StyledDiv = styled.div`
  margin-top: 10px;
`;

const TrashWrapper = styled.div`
  margin-top: 10px;
  border-radius: 50%;
  height: 35px;
  width: 35px;
  background-color: ${p => p.theme.colors.theme};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  i {
    color: white;
    cursor: pointer;
  }
  &:hover {
    opacity: 0.7;
  }
`;

const LoadingIcon = styled(LoadingOutlined)`
  margin-top: 10px;
  height: 25px;
  width: 25px;
  color: ${p => p.theme.colors.theme};
`;

const ErrorIcon = styled(ExclamationOutlined)`
  color: white;
`;

const ErrorIconContainer = styled.div`
  margin-top: 10px;
  color: white;
  border-radius: 50%;
  height: 25px;
  width: 25px;
  background-color: ${p => p.theme.colors.publishSteps.error};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Absence = ({
  match,
  history,
  fetchPlants,
  plants,
  serviceCenterId,
  fetchAbsences,
  isLoadingData,
  absences,
  intl,
  hasNext,
  totalDataCount,
  pageSize = 20,
  fetchMore,
  error,
}) => {
  const dispatch = useDispatch();
  const [prevServiceCenterId, setPrevServiceCenterId] = useState();
  const navigate = useNavigate();
  const [plantData, setPlantData] = useState({
    plant: null,
    region: null,
    fse: null,
  });

  useEffect(() => {
    if (!match.params.type) {
      navigate(`${settingsConfig.types.plant}`);
    }
  }, [match.params.type]);

  useEffect(() => {
    if (serviceCenterId && serviceCenterId !== prevServiceCenterId) {
      setPrevServiceCenterId(serviceCenterId);
      fetchPlants({
        serviceCenterId,
      });
    }
  }, [serviceCenterId]);

  const getColumns = () => [
    {
      label: '',
      dataIndex: 'id',
      width: '10px',
      renderValueFn: () => '',
    },
    {
      label: '',
      dataIndex: 'progressIndicator',
      width: '35px',
      renderValueFn: (v) => {
        if (v === absenceStatusses.progress) {
          return <LoadingIcon />;
        } else if (v === absenceStatusses.error) {
          return (
            <ErrorIconContainer>
              <ErrorIcon />
            </ErrorIconContainer>
          );
        }
        return null;
      },
    },
    {
      label: intl.formatMessage({ id: 'holidays.START_DATE' }),
      dataIndex: 'startDate',
      format: 'date',
    },
    {
      label: intl.formatMessage({ id: 'holidays.START_TIME' }),
      renderValueFn: value => moment(value, 'HH:mm:ss').format('HH:mm'),
      dataIndex: 'startTime',
    },
    {
      label: intl.formatMessage({ id: 'holidays.DURATION' }),
      renderValueFn: value => `${value} ${intl.formatMessage({ id: 'holidays.HOURS' })}`,
      dataIndex: 'duration',
    },
    {
      label: intl.formatMessage({ id: 'holidays.DESCRIPTION' }),
      dataIndex: 'description',
    },
    {
      label: '',
      dataIndex: 'id',
      width: '35px',
      renderValueFn: v => <TrashWrapper
        onClick={(e) => {
          e.stopPropagation();
          dispatch(deleteAbsence({ id: v }));
        }}
      >
        <Icon type='trash' size='xs' />
      </TrashWrapper>,
    },
  ];

  const fetchData = (id) => {
    if (id) {
      fetchAbsences({
        pageSize,
        page: 1,
        technicianId: id,
        serviceCenterId,
      });
    }
  };

  return (
    <>
      <ErrorHandler type='notification' error={error} />
      <TypesMenu
        items={[
          {
            title: intl.formatMessage({ id: 'holidays.PLANT' }),
            type: settingsConfig.types.plant,
          },
          {
            title: intl.formatMessage({ id: 'holidays.REGION' }),
            type: settingsConfig.types.region,
          },
          {
            title: intl.formatMessage({ id: 'holidays.FSE' }),
            type: settingsConfig.types.fse,
          },
        ]}
      />
      <>
        <AbsenceToolbar
          type={match.params.type}
          plants={plants}
          fetchData={fetchData}
          setPlantData={setPlantData}
          serviceCenterId={serviceCenterId}
        />
        <StyledDiv>
          <Table
            columns={getColumns()}
            onRowClick={({ id }) => history.push(`edit-absence/${id}`)}
            data={absences || []}
            hasRowSelector={false}
            pageSize={absences?.length}
            rowKey='id'
            hasFooter={hasNext}
            isLoading={isLoadingData}
            customRemainingItems={() => totalDataCount - absences?.length}
            customMoreFunction={fetchMore}
            customMoreCount={pageSize}
          />
        </StyledDiv>
      </>
      <Routes>
        <Route
          path={'/edit-absence/:id?'}
          element={<AbsenceEditSidepanel
            plantData={plantData}
            type={match.params.type}
          />}
        />
      </Routes>
      <Outlet />
    </>
  );
};

Absence.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  fetchPlants: PropTypes.func,
  plants: PropTypes.array,
  serviceCenterId: PropTypes.string,
  intl: PropTypes.object,
  fetchAbsences: PropTypes.func,
  isLoadingData: PropTypes.bool,
  absences: PropTypes.array,
  hasNext: PropTypes.bool,
  totalDataCount: PropTypes.number,
  fetchMore: PropTypes.func,
  pageSize: PropTypes.number,
  error: PropTypes.object,
};

export default injectIntl(withRouter(Absence));
